import type { IRefundQuoteEntry } from "../../../Orchestrator/Recoils/AddonsAtoms";
import { IBookingResponse } from "../../../Orchestrator/Types/Index";
import {
  passengersBlockedByTicket,
  ticketFulfillmentIdsForLeg,
} from "../../../Components/OnwardTravel/nolTicketRefund";
import { nolFulfillmentIds } from "../../../Components/OnwardTravel/onwardRefund";
import { probeRefundOffersAction } from "../Addons/ProbeRefundOffersAction";

/**
 * The Nol pass ids a cancellation must LEAVE OUT, because every selected rider
 * holding them is on a ticket that refunds nothing.
 *
 * The cancel-side twin of NolCardModal's gate (#352). Without it "Cancel
 * booking" released the pass with the ticket and the retailer paid it out on
 * its own rules — AED 40 back on a Saver fare (INT booking WC7574). The commit
 * cancels such a pass for nothing instead (ConfirmCancelClickAction,
 * releaseWithoutRefund); the quote simply leaves it out.
 *
 * SHARED by the quote (CancelBookingInitAction) and the commit
 * (ConfirmCancelClickAction): the two disagreeing is what shipped the earlier
 * shuttle bug.
 *
 * Prices the tickets LIVE rather than reading refundOffersByFulfillmentIdAtom:
 * that cache is cleared on the way into booking flows and absent after a
 * refresh on the cancel page, and "absent" here means "refund the pass". Only
 * POSTs when a selected rider actually holds a pass; /refund-offers only
 * PROPOSES, so the extra call settles nothing.
 *
 * Fails OPEN, like every gate on this rule: no quote, no ticket, or a pass
 * shared with a rider whose ticket DOES refund — the pass stays in.
 */
export async function nolPassesBlockedByTicket(
  booking: IBookingResponse,
  legIds: Iterable<string>,
  passengerIds: string[]
): Promise<Set<string>> {
  const blocked = new Set<string>();
  const bookingId = booking?.booking?.id;
  if (!bookingId) return blocked;

  const riders: { legId: string; passengerId: string; passIds: string[] }[] = [];
  const ticketIds = new Set<string>();
  for (const legId of legIds) {
    for (const passengerId of passengerIds) {
      const passIds = nolFulfillmentIds(booking, legId, [passengerId]);
      if (passIds.length === 0) continue;
      riders.push({ legId, passengerId, passIds });
      ticketFulfillmentIdsForLeg(booking, legId, passengerId).forEach((id) => ticketIds.add(id));
    }
  }
  if (ticketIds.size === 0) return blocked;

  const entries: Record<string, IRefundQuoteEntry> = {};
  try {
    if (!(await probeRefundOffersAction.quoteInto(entries, bookingId, [...ticketIds]))) return blocked;
  } catch (error) {
    // Never abort a cancellation over an optional gate — fail open.
    console.warn("[nolPassesBlockedByTicket] ticket quote threw; keeping every pass", error);
    return blocked;
  }
  const quotes = { bookingId, entries };

  const allowed = new Set<string>();
  for (const { legId, passengerId, passIds } of riders) {
    const isBlocked = passengersBlockedByTicket(quotes, booking, legId, [passengerId]).has(passengerId);
    passIds.forEach((id) => (isBlocked ? blocked : allowed).add(id));
  }
  allowed.forEach((id) => blocked.delete(id));
  return blocked;
}
